/**
 * @CODE:ROOMS-API-001
 * Rooms API Layer
 *
 * Core API functions for room data:
 * - getRoomsByAccommodationId: Fetch all rooms for a specific accommodation
 * - Error handling and data transformation
 */

import { getSupabaseClient } from "@/lib/supabase";
import type { Room, RoomResponse, RoomError } from "@/features/accommodation/types";

/**
 * Fetch all active rooms for a specific accommodation
 *
 * Fetches from rooms table with filters:
 * - accommodation_id match
 * - is_active = true (only active rooms)
 * - Ordered by legacy_ro_index (room order)
 *
 * @param accommodationId - Accommodation UUID to filter rooms
 * @returns Room data with all fields
 *
 * @example
 * ```
 * const result = await getRoomsByAccommodationId('43adcf50-960b-4d37-8900-61257162e640');
 * console.log(result.data); // Array of Room objects
 * ```
 */
export async function getRoomsByAccommodationId(
  accommodationId: string
): Promise<RoomResponse> {
  try {
    const client = getSupabaseClient();

    // Build query with room_images join
    const { data, error } = await client
      .from("rooms")
      .select(
        `
        *,
        room_images:room_images(
          id,
          room_id,
          image_url,
          storage_path,
          image_order,
          alt_text,
          is_main_photo,
          file_size,
          width,
          height,
          created_at,
          updated_at
        )
      `
      )
      .eq("accommodation_id", accommodationId)
      .eq("is_active", true)
      .order("legacy_ro_index", { ascending: true });

    if (error) {
      console.error("Supabase error fetching rooms:", error);
      throw new Error(error.message);
    }

    // Transform data to match Room type (rename room_images to images)
    const transformedData = data?.map((room: any) => ({
      ...room,
      images: room.room_images || [],
    })) || [];

    return {
      data: transformedData,
      total: transformedData.length,
    };
  } catch (error) {
    console.error("Error fetching rooms:", error);
    const roomError: RoomError = {
      message: error instanceof Error ? error.message : "Failed to fetch rooms",
      code: "ROOMS_FETCH_ERROR",
    };
    throw roomError;
  }
}

/**
 * Format price as Korean Won
 * Helper function for displaying room prices
 *
 * @param price - Price in KRW
 * @returns Formatted price string
 *
 * @example
 * ```
 * formatPrice(120000) // "₩120,000"
 * ```
 */
export function formatRoomPrice(price: number | null | undefined): string {
  if (price === null || price === undefined) {
    return "가격 문의";
  }

  return new Intl.NumberFormat("ko-KR", {
    style: "currency",
    currency: "KRW",
    maximumFractionDigits: 0,
  }).format(price);
}
